import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Typography
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import { AuthWrapper, AuthWrapperProps } from "../Pages/AuthWrapper";
import { useEula } from "./userInvitationUtils";

const pageDetails: AuthWrapperProps = {
  title: "Register your account"
};

interface EULAProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  loading?: boolean;
  onAccept: () => Promise<void> | void;
  allowCancel?: boolean;
  errorMessage?: string | null;
  onCancel?: () => Promise<void> | void; // Optional callback for cancel action (e.g., sign out)
}

export const EULA = ({
  open,
  setOpen,
  loading,
  onAccept,
  allowCancel = true,
  errorMessage,
  onCancel
}: EULAProps) => {
  //TODO: add translations to files
  const { t } = useTranslation();
  // Only fetch EULA when modal is open (requires authentication)
  const { eula, status: fetchEulaStatus, error: fetchEulaError } = useEula(open);
  const [viewedFullEula, setViewedFullEula] = useState(false);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const targetScrollHeight = Math.round(event.currentTarget.scrollHeight);
    const targetScrollTop = Math.round(event.currentTarget.scrollTop);
    const targetClientHeight = event.currentTarget.clientHeight;
    // Get scroll distance
    const difference1 = targetScrollHeight - targetScrollTop;
    // Get distance to end
    const difference2 = difference1 - targetClientHeight;

    // The new screen was presenting an issue where, even though scrolled to the bottom,
    // said there was still one pixel difference and couldn't be reached.
    // I gave it a 3px buffer to be safe.
    const scrolledToBottom = difference2 <= 3;
    if (scrolledToBottom) {
      setViewedFullEula(true);
    }
  };

  const handleCloseEula = (event: object, reason: string) => {
    if (reason === "backdropClick") return;
    if (reason === "escapeKeyDown") return;
  };

  useEffect(() => {
    if (fetchEulaStatus === "succeeded") {
      setViewedFullEula(false);
    }
  }, [fetchEulaStatus]);

  useEffect(() => {
    setViewedFullEula(false);
  }, [open]);

  // Don't render anything if modal is not open
  if (!open) {
    return null;
  }

  if (fetchEulaStatus === "loading") {
    return (
      <Modal
        open={open}
        onClose={handleCloseEula}
        aria-labelledby="eula-modal"
        aria-describedby="accept-or-decline-the-eula"
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column"
          }}
        >
          <CircularProgress />
        </Box>
      </Modal>
    );
  }

  if (fetchEulaStatus === "failed" || fetchEulaError) {
    return (
      <Modal
        open={open}
        onClose={handleCloseEula}
        aria-labelledby="eula-modal"
        aria-describedby="accept-or-decline-the-eula"
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column"
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 700,
              backgroundColor: "white",
              padding: 2
            }}
          >
            <Typography variant="h2">{t("Error")}</Typography>
            <Typography variant="body1" sx={{ marginTop: 2 }}>
              {fetchEulaError || "Unable to load EULA text. Please try again later."}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setOpen(false)}
              sx={{ marginTop: 2 }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }

  if (fetchEulaStatus === "succeeded") {
    return (
      <Modal
        open={open}
        onClose={handleCloseEula}
        aria-labelledby="eula-modal"
        aria-describedby="accept-or-decline-the-eula"
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column"
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 700,
              height: 40,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              overflow: "auto",
              backgroundColor: "white",
              padding: 1
            }}
          >
            {t(
              "Please read the following end user license agreement and scroll down to the end."
            )}
          </Box>
          <Box
            sx={{
              width: "100%",
              maxWidth: 700,
              height: 400,
              overflow: "auto",
              backgroundColor: "white",
              padding: 2
            }}
            onScroll={handleScroll}
          >
            {eula && (
              <div
                className="eula-content"
                dangerouslySetInnerHTML={{ __html: eula?.formattedText }}
              />
            )}
          </Box>
          <Box
            sx={{
              width: 700,
              height: 120,
              overflow: "auto",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              backgroundColor: "white",
              padding: 1
            }}
          >
            {errorMessage && (
              <Grid xs={12} sx={{ paddingBottom: "10px" }}>
                <Alert severity="error">{errorMessage}</Alert>
              </Grid>
            )}
            <Grid xs={12}>
              <LoadingButton
                variant="contained"
                loading={loading}
                disabled={loading || !viewedFullEula}
                onClick={() => {
                  onAccept();
                }}
              >
                {loading ? t("Processing...") : t("I Agree")}
              </LoadingButton>
            </Grid>

            {allowCancel && (
              <Grid xs={12} sx={{ paddingTop: "10px" }}>
                <Button
                  variant="outlined"
                  disabled={loading}
                  onClick={async () => {
                    if (onCancel) {
                      await onCancel();
                    }
                    setOpen(false);
                  }}
                >
                  {t("Cancel")}
                </Button>
              </Grid>
            )}
          </Box>
        </Box>
      </Modal>
    );
  }
  return <div>No criteria met</div>;
};
